import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Polyline, Marker, InfoWindow, Polygon, OverlayView } from '@react-google-maps/api';
import { selectSelectedRoute } from '../../../features/tripcenter/routeSlice';

import { useDispatch } from 'react-redux';
import { setMapControl, setZoom, toggleVehicleIcons, setNeedBounding } from '../../../features/map/mapSlice';
import { clearVisibleAssetsMarkers, restoreVisibleAssetsMarkers, removeDetailView } from '../../../features/assets/assetsSlice';

const routePathOptions = {
    strokeColor: 'Pink', //'Crimson',//'#505050',
    strokeOpacity: 0.8,
    strokeWeight: 6,
    icons: [
        {
            icon: {
                path: 'M 0,-1 0,1',
                strokeOpacity: 1,
                scale: 4,
                strokeColor: 'Purple',//'#909090',
                strokeWeight: 3
            },
            repeat: '20px',
            offset: '0'
        },
        {
            icon: {
                path: window.google && window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 3,
                strokeColor: '#311B92',
                fillColor: '#311B92',
                fillOpacity: 1,
                strokeWeight: 2
            },
            offset: '25%',
        },
        {
            icon: {
                path: window.google && window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 3,
                strokeColor: '#311B92',
                fillColor: '#311B92',
                fillOpacity: 1,
                strokeWeight: 2
            },
            offset: '50%',
        },
        {
            icon: {
                path: window.google && window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 3,
                strokeColor: '#311B92',
                fillColor: '#311B92',
                fillOpacity: 1,
                strokeWeight: 2
            },
            offset: '75%',
        },
        {
            icon: {
                path: window.google && window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 3,
                strokeColor: '#311B92',
                fillColor: '#311B92',
                fillOpacity: 1,
                strokeWeight: 2
            },
            offset: '100%',
        }
    ],
    clickable: false,
    draggable: false,
    editable: false,
    visible: true,
    zIndex: 1
};
const generateSvgMarker = (color, geometry, google) => {
    // SVG paths for different geometries (centered around 0,0 in a 10x10 viewport)
    const paths = {
        square: "M-5,-5 L5,-5 L5,5 L-5,5 Z",
        hexagon: "M-3,-5 L3,-5 L6,0 L3,5 L-3,5 L-6,0 Z",
        star: "M0,-5 L1.5,-1.5 L5,-1.5 L2.5,1 L3.5,5 L0,2.5 L-3.5,5 L-2.5,1 L-5,-1.5 L-1.5,-1.5 Z"
    };

    // Validate geometry parameter
    if (!paths[geometry]) {
        return {
            path: paths[geometry],
            fillColor: color,
            fillOpacity: 1,
            strokeColor: 'white',
            strokeWeight: 2,
            rotation: 0,
            scale: 2,
            // Center anchor point for all shapes
            anchor: new google.maps.Point(0, 0)
        };
    }

    return {
        path: paths[geometry],
        fillColor: color,
        fillOpacity: 1,
        strokeColor: 'white',
        strokeWeight: 2,
        rotation: 0,
        scale: 2,
        // Center anchor point for all shapes
        anchor: new google.maps.Point(0, 0)
    };
};


const GoogleRouteView = ({ map }) => {
    const selectedRoute = useSelector(selectSelectedRoute);
    //console.log("Selected route", selectedRoute);
    const dispatch = useDispatch();
    /* useEffect(() => {
        if (map && selectedRoute && selectedRoute.routePath && selectedRoute.routePath.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            selectedRoute.routePath.forEach(point => {
                bounds.extend({ lat: point.lat, lng: point.lng });
            });
            map.fitBounds(bounds);
        }
    }, [map, selectedRoute]); */

    useEffect(() => {
        if (selectedRoute && map) {
            dispatch(setMapControl('route'));
            // Removed dispatch(removeDetailView()) to keep the focused asset when route is selected
            dispatch(clearVisibleAssetsMarkers());
            dispatch(toggleVehicleIcons(true));
            dispatch(setZoom(10));
            dispatch(setNeedBounding(true));

            // Add map styling to dim the background
            map.setOptions({
                styles: [
                    {
                        featureType: "all",
                        elementType: "all",
                        stylers: [
                            { saturation: -50 },  // Reduce saturation
                            { lightness: 10 }     // Slightly increase lightness
                        ]
                    },
                    {
                        featureType: "road",
                        elementType: "geometry",
                        stylers: [
                            { saturation: -30 },  // Less desaturation for roads
                            { lightness: 20 }     // Make roads lighter
                        ]
                    }
                ]
            });
        }

        return () => {
            dispatch(setMapControl('asset'));
            dispatch(restoreVisibleAssetsMarkers());
            dispatch(toggleVehicleIcons(true));
            dispatch(setNeedBounding(false));

            // Reset map styles when component unmounts
            if (map) {
                map.setOptions({
                    styles: []  // Reset to default styles
                });
            }
        }
    }, [dispatch, selectedRoute, map]);

    // Clean up map drawings on unmount
    useEffect(() => {
        return () => {
            if (map) {
                // Get all overlays currently on the map
                const overlays = map.getOverlays?.();
                if (overlays) {
                    // Remove each overlay
                    overlays.forEach(overlay => {
                        if (overlay) {
                            overlay.setMap(null);
                        }
                    });
                }
            }
        };
    }, [map]);

    const [isStartPointInfoOpen, setIsStartPointInfoOpen] = useState(false);
    const [isEndPointInfoOpen, setIsEndPointInfoOpen] = useState(false);
    const [viaPointInfoOpen, setViaPointInfoOpen] = useState({});

    const routePath = selectedRoute?.routePath;
    const startLocation = selectedRoute?.startLocation;
    const endLocation = selectedRoute?.endLocation;
    const viaLocations = selectedRoute?.viaLocations;
    const routeZones = selectedRoute?.routeZones;
    const routeMarkers = selectedRoute?.routeMarkers;

    const sanitizePath = (path) => {
        if (path && path.coordinates && path.coordinates.length > 0) {
            return path.coordinates.reduce((validPoints, point) => {
                const lat = parseFloat(point.lat);
                const lng = parseFloat(point.lng);
                if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
                    validPoints.push({ lat, lng });
                }
                return validPoints;
            }, []);
        }
    };

    const sanitizedStartLocation = startLocation ? {
        point: {
            lat: parseFloat(startLocation?.lat),
            lng: parseFloat(startLocation?.lng),
        },
        locationName: startLocation?.locationName,
        address: startLocation?.address,
        locationType: startLocation?.locationType,
        zoneCoordinates: startLocation?.zoneCoordinates?.map(coordinate => ({
            lat: parseFloat(coordinate[1]),
            lng: parseFloat(coordinate[0])
        }))
    } : null;
    const sanitizedEndLocation = endLocation ? {
        point: {
            lat: parseFloat(endLocation?.lat),
            lng: parseFloat(endLocation?.lng),
        },
        locationName: endLocation?.locationName,
        address: endLocation?.address,
        locationType: endLocation?.locationType,
        zoneCoordinates: endLocation?.zoneCoordinates?.map(coordinate => ({
            lat: parseFloat(coordinate[1]),
            lng: parseFloat(coordinate[0])
        }))
    } : null;
    const sanitizedViaLocations = viaLocations ? viaLocations.map(location => ({
        point: {
            lat: parseFloat(location?.lat),
            lng: parseFloat(location?.lng),
        },
        locationName: location?.locationName,
        address: location?.address,
        locationType: location?.locationType,
        zoneCoordinates: location?.zoneCoordinates?.map(coordinate => ({
            lat: parseFloat(coordinate[1]),
            lng: parseFloat(coordinate[0])
        }))
    })) : null;

    /*
    {
    "zone_id": 31504,
    "zone_name": "Vadodara Toll Plaza",
    "zone_color": "#1AC1A8",
    "zone_visible": "true",
    "zone_name_visible": "true",
    "zone_coords": [
        {
            "lat": 22.25640824140149,
            "lng": 73.10970774309946
        },
        {
            "lat": 22.256214615668423,
            "lng": 73.1090479196818
        },
        {
            "lat": 22.255206764579572,
            "lng": 73.1094985307963
        },
        {
            "lat": 22.255464934022573,
            "lng": 73.11032465117289
        }
    ],
    "zone_tags": [
        "Toll Plaza"
    ]
}
    */
    const sanitizedRouteZones = routeZones ? routeZones.map(zone => ({
        locationName: zone.zone_name,
        zoneColor: zone.zone_color,
        zoneVisible: zone.zone_visible,
        zoneNameVisible: zone.zone_name_visible,
        zoneCoords: zone.zone_coords.map(coord => ({
            lat: parseFloat(coord.lat),
            lng: parseFloat(coord.lng)
        })).filter(coord => !isNaN(coord.lat) && !isNaN(coord.lng)),
        zoneTags: zone.zone_tags
    })).filter(zone => zone.zoneCoords.length >= 3) : null;
    //console.log("sanitizedRouteZones", sanitizedRouteZones);
    //console.log("First zone coords:", sanitizedRouteZones?.[0]?.zoneCoords);
    const sanitizedRouteMarkers = routeMarkers ? routeMarkers.map(marker => ({
        point: {
            lat: parseFloat(marker.lat),
            lng: parseFloat(marker.lng),
        }
    })) : null;

    const sanitizedRoutePath = routePath ? sanitizePath(routePath) : null;

    if (selectedRoute && selectedRoute.routePath && selectedRoute.routePath.length > 0) {
        return (
            <Fragment>
                <Polyline
                    path={selectedRoute.routePath}
                    options={{
                        ...routePathOptions,
                        paths: sanitizedRoutePath,
                    }}
                />
                {sanitizedStartLocation && (
                    <Marker
                        icon={generateSvgMarker("DodgerBlue", "hexagon", window.google)}
                        position={sanitizedStartLocation.point}
                        onClick={() => setIsStartPointInfoOpen(true)}
                    >
                        {isStartPointInfoOpen && (
                            <InfoWindow
                                position={sanitizedStartLocation.point}
                                onCloseClick={() => setIsStartPointInfoOpen(false)}
                            >
                                <div style={{
                                    padding: '8px',
                                    borderRadius: '8px',
                                    maxWidth: '300px',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                                }}>
                                    <h3 style={{
                                        margin: '0 0 12px 0',
                                        color: '#1a237e',
                                        borderBottom: '2px solid #e0e0e0',
                                        paddingBottom: '8px'
                                    }}>Start: {sanitizedStartLocation.locationName}</h3>

                                    <div style={{ display: 'grid', gap: '8px' }}>
                                        {sanitizedStartLocation.address !== sanitizedStartLocation.locationName && (
                                            <div style={{ color: '#666', marginBottom: '8px' }}>{sanitizedStartLocation.address}</div>
                                        )}
                                    </div>
                                </div>
                            </InfoWindow>
                        )}
                    </Marker>
                )}
                {sanitizedStartLocation && sanitizedStartLocation.zoneCoordinates && (
                    <Polygon
                        paths={sanitizedStartLocation.zoneCoordinates}
                        options={{
                            fillColor: "DodgerBlue",
                            fillOpacity: 0.35,
                            strokeColor: "white",
                            strokeWeight: 2
                        }}
                    />
                )}

                {sanitizedEndLocation && (
                    <Marker
                        icon={generateSvgMarker("Navy", "hexagon", window.google)}
                        position={sanitizedEndLocation.point}
                        onClick={() => setIsEndPointInfoOpen(true)}
                    >
                        {isEndPointInfoOpen && (
                            <InfoWindow
                                position={sanitizedEndLocation.point}
                                onCloseClick={() => setIsEndPointInfoOpen(false)}
                            >
                                <div style={{
                                    padding: '8px',
                                    borderRadius: '8px',
                                    maxWidth: '300px',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                                }}>
                                    <h3 style={{
                                        margin: '0 0 12px 0',
                                        color: '#1a237e',
                                        borderBottom: '2px solid #e0e0e0',
                                        paddingBottom: '8px'
                                    }}>End: {sanitizedEndLocation.locationName}</h3>

                                    <div style={{ display: 'grid', gap: '8px' }}>
                                        {sanitizedEndLocation.address !== sanitizedEndLocation.locationName && (
                                            <div style={{ color: '#666', marginBottom: '8px' }}>{sanitizedEndLocation.address}</div>
                                        )}
                                    </div>
                                </div>
                            </InfoWindow>
                        )}
                    </Marker>
                )}
                {sanitizedEndLocation && sanitizedEndLocation.zoneCoordinates && (
                    <Polygon
                        paths={sanitizedEndLocation.zoneCoordinates}
                        options={{
                            fillColor: "Navy",
                            fillOpacity: 0.35,
                            strokeColor: "white",
                            strokeWeight: 2
                        }}
                    />
                )}

                {sanitizedViaLocations && sanitizedViaLocations.length > 0 && sanitizedViaLocations.map((sanitizedViaLocation, index) => (
                    <Fragment key={index}>
                        <Marker
                            icon={generateSvgMarker("Orchid", "square", window.google)}
                            position={sanitizedViaLocation.point}
                            onClick={() => setViaPointInfoOpen(prev => ({ ...prev, [index]: true }))}
                        >
                            {viaPointInfoOpen[index] && (
                                <InfoWindow
                                    position={sanitizedViaLocation.point}
                                    onCloseClick={() => setViaPointInfoOpen(prev => ({ ...prev, [index]: false }))}
                                >
                                    <div style={{
                                        padding: '8px',
                                        borderRadius: '8px',
                                        maxWidth: '300px',
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                                    }}>
                                        <h3 style={{
                                            margin: '0 0 12px 0',
                                            color: '#1a237e',
                                            borderBottom: '2px solid #e0e0e0',
                                            paddingBottom: '8px'
                                        }}>Stop {index + 1}: {sanitizedViaLocation.locationName}</h3>

                                        <div style={{ display: 'grid', gap: '8px' }}>
                                            {sanitizedViaLocation.address !== sanitizedViaLocation.locationName && (
                                                <div style={{ color: '#666', marginBottom: '8px' }}>{sanitizedViaLocation.address}</div>
                                            )}
                                        </div>
                                    </div>
                                </InfoWindow>
                            )}
                        </Marker>
                        {sanitizedViaLocation.zoneCoordinates && (
                            <Polygon
                                paths={sanitizedViaLocation.zoneCoordinates}
                                options={{
                                    fillColor: "Orchid",
                                    fillOpacity: 0.35,
                                    strokeColor: "white",
                                    strokeWeight: 2
                                }}
                            />
                        )}
                    </Fragment>
                ))}
                {sanitizedRouteZones && sanitizedRouteZones.length > 0 && sanitizedRouteZones.map((sanitizedRouteZone, index) => {
                    //console.log(`Rendering zone ${index}:`, sanitizedRouteZone.zoneCoords);
                    // Ensure we have valid coordinates for a polygon (at least 3 points)
                    if (!sanitizedRouteZone.zoneCoords || sanitizedRouteZone.zoneCoords.length < 3) {
                        console.warn(`Zone ${index} has insufficient coordinates:`, sanitizedRouteZone.zoneCoords);
                        return null;
                    }

                    // Calculate center point for label
                    const centerLat = sanitizedRouteZone.zoneCoords.reduce((sum, coord) => sum + coord.lat, 0) / sanitizedRouteZone.zoneCoords.length;
                    const centerLng = sanitizedRouteZone.zoneCoords.reduce((sum, coord) => sum + coord.lng, 0) / sanitizedRouteZone.zoneCoords.length;

                    // Helper function to determine if a color is bright or dark
                    const isColorBright = (hexColor) => {
                        if (!hexColor || typeof hexColor !== 'string') return false;
                        let hex = hexColor.replace('#', '');
                        if (hex.length === 3) {
                            hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
                        }
                        const r = parseInt(hex.substring(0, 2), 16);
                        const g = parseInt(hex.substring(2, 4), 16);
                        const b = parseInt(hex.substring(4, 6), 16);
                        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
                        return luminance > 0.6;
                    };

                    return (
                        <Fragment key={index}>
                            <Polygon
                                paths={sanitizedRouteZone.zoneCoords}
                                options={{
                                    fillColor: sanitizedRouteZone.zoneColor || "#CF3827",
                                    fillOpacity: 0.3,
                                    strokeColor: sanitizedRouteZone.zoneColor || "#CF3827",
                                    strokeOpacity: 1,
                                    strokeWeight: 2,
                                    clickable: true,
                                    draggable: false,
                                    editable: false,
                                    geodesic: false,
                                    zIndex: 1
                                }}
                            />
                            <Marker
                                icon={{
                                    path: window.google.maps.SymbolPath.CIRCLE,
                                    fillColor: 'yellow',
                                    fillOpacity: 0.75,
                                    strokeColor: 'red',
                                    strokeWeight: 2,
                                    scale: 6
                                }}
                                position={{ lat: centerLat, lng: centerLng }}
                                title={sanitizedRouteZone.locationName}
                            />
                            <OverlayView
                                position={{ lat: centerLat, lng: centerLng }}
                                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                            >
                                <div
                                    style={{
                                        backgroundColor: `${sanitizedRouteZone.zoneColor || '#CF3827'}DD`,
                                        position: 'absolute',
                                        left: 10,
                                        top: -16,
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: isColorBright(sanitizedRouteZone.zoneColor || '#CF3827') ? '#000' : '#fff',
                                        border: `2px solid ${sanitizedRouteZone.zoneColor || '#CF3827'}`,
                                        maxWidth: '150px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        transform: 'translateX(-50%)',
                                        textAlign: 'center',
                                    }}>
                                    {sanitizedRouteZone.locationName}
                                </div>
                            </OverlayView>
                        </Fragment>
                    );
                })}
                {sanitizedRouteMarkers && sanitizedRouteMarkers.length > 0 && sanitizedRouteMarkers.map((sanitizedRouteMarker, index) => (
                    <Marker
                        icon={generateSvgMarker("Orchid", "square", window.google)}
                        position={sanitizedRouteMarker.point}
                    />
                ))}
            </Fragment>
        )
    } else {
        //console.log("No route path");
        return null;
    }
}

export default GoogleRouteView;
